import { useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button, cn } from "@/core/ui";
import { formatDuration } from "./time";

/**
 * What a solve looks like.
 *
 * A puzzle here is ranked on `elapsed_ms` — or, for a type that declares `scored`, on its
 * points with the clock breaking ties (CLAUDE.md §11, ADR 0040) — and the clock is already
 * the loudest element of the play page. So the celebration is that clock landing — it punches
 * once and freezes — plus what the clock cannot say: the placing, and the points when they
 * exist. Paper flies off the sheet from the clock itself, which is what ties the two together.
 *
 * It is core chrome, like the timer and the submit button: a puzzle type never draws its own
 * (§7.4). And it is a moment, not a state — `fresh` is false when the page merely opens on an
 * attempt that was already solved, and then nothing moves.
 */
export function SolveCelebration({
  elapsedMs,
  rank,
  score,
  fresh,
  originRef,
  onReplay,
  replaying,
}: {
  elapsedMs: number;
  /** Placing on the leaderboard, or null for a practice run and while it is still unknown. */
  rank: number | null;
  /** Points of the solve for a scored type, null when the type has none (ADR 0040). */
  score: number | null;
  /** This page instance watched the solve happen, so the burst is owed. */
  fresh: boolean;
  /** What the paper is thrown from: the clock. */
  originRef: RefObject<HTMLElement>;
  onReplay: () => void;
  replaying: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <div
        className={cn("bt-play__result", fresh && "bt-play__result--fresh")}
        role="status"
        aria-live="polite"
      >
        {rank !== null ? (
          <p className={cn("bt-play__rank", rank === 1 && "bt-play__rank--first")}>
            <span className="bt-play__rank-figure">
              {t("play.rank", { count: rank, ordinal: true })}
            </span>
            <span className="bt-play__rank-caption">{t("play.rank_caption")}</span>
          </p>
        ) : null}
        {score !== null ? (
          <p className="bt-play__score">
            <span className="bt-play__score-figure">{score}</span>
            <span className="bt-play__score-caption">
              {t("play.points_caption", { count: score })}
            </span>
          </p>
        ) : null}
        <p className="bt-play__result-line">
          {t("play.solved_in", { time: formatDuration(elapsedMs) })}
        </p>
        <Button variant="outline" onClick={onReplay} loading={replaying}>
          {t("play.replay")}
        </Button>
      </div>
      {fresh ? <Confetti originRef={originRef} /> : null}
    </>
  );
}

/* --- The paper ---
 *
 * Drawn on a canvas rather than with DOM nodes: a hundred and thirty elements animating at
 * once is the one place in this app where the compositor needs the help.
 *
 * The chips are cut from role tokens declared on the canvas by play.css, never from literals,
 * so the burst follows the tenant's colour and the current theme like everything else
 * (CLAUDE.md §12). The canvas is portalled to <body> so no ancestor's clip can eat it, and it
 * removes itself when the last chip has fallen.
 */

const CHIP_COUNT = 130;
const LIFE_MS = 3200;
const FADE_MS = 700;
const GRAVITY = 0.00085; // px per ms²
const DRAG = 0.0011; // per ms
/** Spread of the throw. All 130 at once is a single flash; over a quarter second it pours. */
const STAGGER_MS = 280;
/** A backgrounded tab hands back one enormous frame; clamping it stops the paper teleporting. */
const MAX_STEP_MS = 34;

interface Chip {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  angle: number;
  spin: number;
  /** Phase of the tumble. Squashing the chip vertically by its cosine reads as a flat piece
      of paper turning over, without the cost of drawing it in three dimensions. */
  flip: number;
  flipRate: number;
  color: string;
  /** Held back this long before it is thrown. */
  delay: number;
}

/** The four chips play.css cuts the burst from. Brand first: the rest fill in behind it. */
const SLOTS = ["brand", "accolade", "success", "ink"];

function readPalette(canvas: HTMLCanvasElement): string[] {
  const styles = getComputedStyle(canvas);
  return SLOTS.map((slot) => styles.getPropertyValue(`--bt-confetti-${slot}`).trim()).filter(
    Boolean,
  );
}

function spawn(palette: string[], origin: DOMRect | null): Chip[] {
  const width = typeof window === "undefined" ? 0 : window.innerWidth;
  const height = typeof window === "undefined" ? 0 : window.innerHeight;
  const ox = origin ? origin.left + origin.width / 2 : width / 2;
  const oy = origin ? origin.top + origin.height / 2 : height * 0.25;
  const spread = (origin?.width ?? 0) + 24;
  // The clock sits flush right in the header, so an even fan would throw half the paper out
  // of the window. Lean the whole burst back towards the middle of the page instead.
  const inward = width > 0 ? (width / 2 - ox) / (width / 2) : 0;
  const chips: Chip[] = [];
  for (let i = 0; i < CHIP_COUNT; i += 1) {
    // Mostly straight up, in a wide fan: the clock throws, gravity does the rest.
    const direction = -Math.PI / 2 + (Math.random() - 0.5) * 2;
    // Enough to arc well above the clock, not enough to leave by the top of the window.
    const speed = 0.28 + Math.random() * 0.42;
    const w = 4 + Math.random() * 6;
    chips.push({
      x: ox + (Math.random() - 0.5) * spread,
      y: oy + (Math.random() - 0.5) * 12,
      vx: Math.cos(direction) * speed * 1.5 + inward * speed * 0.5,
      vy: Math.sin(direction) * speed,
      w,
      h: w * (0.6 + Math.random() * 1.6),
      angle: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.006,
      flip: Math.random() * Math.PI,
      flipRate: 0.002 + Math.random() * 0.004,
      delay: Math.random() * STAGGER_MS,
      // The tenant's own colour leads; the three system hues fill in behind it.
      color:
        palette[Math.random() < 0.4 ? 0 : 1 + Math.floor(Math.random() * (palette.length - 1))],
    });
  }
  return chips;
}

function Confetti({ originRef }: { originRef: RefObject<HTMLElement> }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [done, setDone] = useState(false);
  // Read once, at the moment of the solve: the clock may scroll away mid-flight.
  const origin = useRef<DOMRect | null>(null);
  if (origin.current === null && originRef.current)
    origin.current = originRef.current.getBoundingClientRect();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // CLAUDE.md §12: the app stays readable with reduced motion. The result is already in the
    // band above; the paper is the part nobody needs.
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setDone(true);
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return; // no 2d context (jsdom, or a canvas-less browser): skip, silently
    const palette = readPalette(canvas);
    if (palette.length === 0) return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    ctx.scale(dpr, dpr);

    const chips = spawn(palette, origin.current);
    const start = performance.now();
    let previous = start;
    let frame = 0;

    const draw = (now: number) => {
      const dt = Math.min(now - previous, MAX_STEP_MS);
      previous = now;
      const age = now - start;
      const fade = age > LIFE_MS - FADE_MS ? Math.max(0, (LIFE_MS - age) / FADE_MS) : 1;
      ctx.clearRect(0, 0, width, height);
      for (const chip of chips) {
        if (age < chip.delay) continue;
        chip.vy += GRAVITY * dt;
        chip.vx *= 1 - DRAG * dt;
        chip.vy *= 1 - DRAG * dt;
        chip.flip += chip.flipRate * dt;
        chip.vx += Math.sin(chip.flip) * 0.00018 * dt; // flutter, so nothing falls straight
        chip.x += chip.vx * dt;
        chip.y += chip.vy * dt;
        chip.angle += chip.spin * dt;
        if (chip.y - chip.h > height) continue;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(chip.x, chip.y);
        ctx.rotate(chip.angle);
        ctx.scale(1, Math.cos(chip.flip));
        ctx.fillStyle = chip.color;
        ctx.fillRect(-chip.w / 2, -chip.h / 2, chip.w, chip.h);
        ctx.restore();
      }
      if (age < LIFE_MS) frame = requestAnimationFrame(draw);
      else setDone(true);
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  if (done) return null;
  return createPortal(
    <canvas ref={canvasRef} className="bt-confetti" aria-hidden="true" />,
    document.body,
  );
}
